/**
 * Plan selection component for choosing between monthly and quarterly subscription plans.
 * Features a toggle switch design similar to modern subscription interfaces.
 */

import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Plan, SUBSCRIPTION_PLANS } from '../config/plan-config';

interface PlanSelectionProps {
  selectedPlanId: string;
  onPlanSelect: (planId: string) => void;
  onContinue?: () => void;
  className?: string;
}

export const PlanSelection: React.FC<PlanSelectionProps> = ({
  selectedPlanId,
  onPlanSelect,
  onContinue,
  className = '',
}) => {
  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const getSavingsText = (plan: Plan) => {
    if (plan.interval === 'quarter') {
      const monthlyPlan = SUBSCRIPTION_PLANS.find((p) => p.interval === 'month');
      if (monthlyPlan) {
        const quarterlyEquivalent = monthlyPlan.price * 3;
        const savings = quarterlyEquivalent - plan.price;
        const savingsPercent = Math.round((savings / quarterlyEquivalent) * 100);
        return `-${savingsPercent}%`;
      }
    }
    return null;
  };

  const getSavingsAmount = (plan: Plan) => {
    if (plan.interval === 'quarter') {
      const monthlyPlan = SUBSCRIPTION_PLANS.find((p) => p.interval === 'month');
      if (monthlyPlan) {
        const quarterlyEquivalent = monthlyPlan.price * 3;
        const savings = quarterlyEquivalent - plan.price;
        return formatPrice(savings, plan.currency);
      }
    }
    return null;
  };

  const monthlyPlan = SUBSCRIPTION_PLANS.find((p) => p.interval === 'month');
  const quarterlyPlan = SUBSCRIPTION_PLANS.find((p) => p.interval === 'quarter');

  if (!monthlyPlan || !quarterlyPlan) return null;

  const isMonthlySelected = selectedPlanId === monthlyPlan.id;
  const isQuarterlySelected = selectedPlanId === quarterlyPlan.id;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-kind-text-primary mb-2">Upgrade to Premium</h2>
        <p className="text-kind-text-secondary">Choose the plan that works best for you</p>
      </div>

      {/* Plan Toggle */}
      <div className="flex justify-center">
        <div className="bg-kind-container rounded-full p-1 flex">
          <button
            onClick={() => onPlanSelect(monthlyPlan.id)}
            className={`
              px-6 py-3 rounded-full font-medium transition-all duration-200 flex items-center gap-2
              ${
                isMonthlySelected
                  ? 'bg-violet-500 text-white shadow-lg'
                  : 'text-kind-text-secondary hover:text-kind-text-primary'
              }
            `}
          >
            Monthly
          </button>
          <button
            onClick={() => onPlanSelect(quarterlyPlan.id)}
            className={`
              px-6 py-3 rounded-full font-medium transition-all duration-200 flex items-center gap-2
              ${
                isQuarterlySelected
                  ? 'bg-violet-500 text-white shadow-lg'
                  : 'text-kind-text-secondary hover:text-kind-text-primary'
              }
            `}
          >
            Quarterly
            {getSavingsText(quarterlyPlan) && (
              <span className="text-sm bg-violet-600 px-2 py-1 rounded-full text-white">
                {getSavingsText(quarterlyPlan)}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Selected Plan Details */}
      <div className="bg-kind-graphite rounded-2xl border border-gray-300 dark:border-white/10 p-8 max-w-md mx-auto">
        {isMonthlySelected && (
          <div className="text-center">
            <h3 className="text-2xl font-bold text-kind-text-primary mb-2">{monthlyPlan.name}</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-kind-text-primary">
                {formatPrice(monthlyPlan.price, monthlyPlan.currency)}
              </span>
              <span className="text-kind-text-secondary ml-2">/month</span>
            </div>
            <p className="text-kind-text-secondary mb-6">{monthlyPlan.description}</p>
          </div>
        )}

        {isQuarterlySelected && (
          <div className="text-center">
            <h3 className="text-2xl font-bold text-kind-text-primary mb-2">{quarterlyPlan.name}</h3>
            <div className="mb-2">
              <span className="text-2xl text-kind-text-secondary line-through">
                {formatPrice(monthlyPlan.price * 3, monthlyPlan.currency)}
              </span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-kind-text-primary">
                {formatPrice(quarterlyPlan.price / 3, quarterlyPlan.currency)}
              </span>
              <span className="text-kind-text-secondary ml-2">/month</span>
            </div>
            <div className="mb-6">
              <span className="text-violet-600 font-medium">
                Saving {getSavingsAmount(quarterlyPlan)} per quarter
              </span>
            </div>
            <p className="text-kind-text-secondary mb-6">{quarterlyPlan.description}</p>
          </div>
        )}

        {/* Features */}
        <div className="space-y-4 mb-8">
          {(isMonthlySelected ? monthlyPlan.features : quarterlyPlan.features)
            .filter((feature) => !feature.includes('Save'))
            .map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-kind-text-secondary">{feature}</span>
              </div>
            ))}
        </div>

        {/* Continue Button */}
        {onContinue && (
          <button
            onClick={onContinue}
            disabled={!selectedPlanId}
            className="w-full bg-violet-500 hover:bg-violet-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2"
          >
            Continue
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};
